import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { useGame } from "../GameState";
import { SFX, resumeAudio } from "../sfx";
import HUD, { AchievementPopup } from "../components/HUD";
import GameControls from "../components/GameControls";

const AVEN_CALM = process.env.PUBLIC_URL + "/mothersday/aven-cutout.png";
const AVEN_LAUGH = process.env.PUBLIC_URL + "/mothersday/aven-laughing-cutout.png";
const MAMA_FACE = process.env.PUBLIC_URL + "/mothersday/aastha-cutout.png";

const AVEN_W = 70;
const AVEN_H = 88;
const GRAVITY = 1500;
const FLAP_V = -480;
const SCROLL_V = 220;
const CLOUD_W = 110;
const CLOUD_H = 60;
const HEART_R = 18;
const GOAL_DISTANCE = 4500;
const STEER_V = 320;

let nextId = 1;
const newWorld = (sw, sh) => ({
  aven: { x: sw / 2, y: sh - 140, vy: 0 },
  hearts: [],
  clouds: [],
  score: 0,
  distance: 0,
  spawnHeart: 0,
  spawnCloud: 0,
});

const BossAct = ({ onNext }) => {
  const { addScore, clearLevel, addAchievement } = useGame();
  const [phase, setPhase] = useState("ready");
  const [, forceRender] = useState(0);
  const [size, setSize] = useState({ w: 520, h: 600 });

  const stageRef = useRef(null);
  const phaseRef = useRef(phase);
  const sizeRef = useRef(size);
  const worldRef = useRef(newWorld(520, 600));
  const keysRef = useRef({ left: false, right: false });

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { sizeRef.current = size; }, [size]);

  /* responsive sizing */
  useEffect(() => {
    const measure = () => {
      const wAvail = Math.min(window.innerWidth - 24, 560);
      const hAvail = Math.min(window.innerHeight - 240, 700);
      // keep ~5/6 portrait ratio but never overflow
      const w = Math.max(280, wAvail);
      const h = Math.max(420, hAvail);
      setSize({ w, h });
      // recenter Aven if outside bounds
      const wo = worldRef.current;
      if (wo.aven.x > w - AVEN_W / 2) wo.aven.x = w - AVEN_W / 2;
      if (wo.aven.x < AVEN_W / 2) wo.aven.x = AVEN_W / 2;
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* keyboard input (always on while mounted) */
  useEffect(() => {
    const flap = () => {
      if (phaseRef.current !== "playing") return;
      worldRef.current.aven.vy = FLAP_V;
      SFX.flap();
    };
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keysRef.current.left = true;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keysRef.current.right = true;
      if (e.code === "Space" || e.key === " " || e.key === "ArrowUp" || e.key === "w" || e.key === "W") {
        e.preventDefault();
        flap();
      }
    };
    const onKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keysRef.current.left = false;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keysRef.current.right = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  /* tap-anywhere flap on stage (excluding control buttons) */
  const onStageTap = (e) => {
    if (phaseRef.current !== "playing") return;
    if (e.target.closest(".md-game-card")) return;
    if (e.target.closest(".md-btn-soft")) return;
    if (e.target.closest(".md-mini-btn")) return;
    if (e.target.closest(".md-help-panel")) return;
    if (e.target.closest(".md-boss-controls")) return;
    worldRef.current.aven.vy = FLAP_V;
    SFX.flap();
  };

  /* main rAF loop */
  useEffect(() => {
    if (phase !== "playing") return;
    let raf;
    let last = performance.now();

    const loop = (now) => {
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      const w = worldRef.current;
      const SW = sizeRef.current.w;
      const SH = sizeRef.current.h;

      // physics
      w.aven.vy += GRAVITY * dt;
      w.aven.y += w.aven.vy * dt;
      if (w.aven.y < 40) { w.aven.y = 40; w.aven.vy = 0; }
      if (w.aven.y > SH - AVEN_H) { w.aven.y = SH - AVEN_H; w.aven.vy = 0; }

      // horizontal steer
      if (keysRef.current.left)  w.aven.x = Math.max(AVEN_W / 2, w.aven.x - STEER_V * dt);
      if (keysRef.current.right) w.aven.x = Math.min(SW - AVEN_W / 2, w.aven.x + STEER_V * dt);

      // distance / progress
      w.distance += SCROLL_V * dt;
      const prog = Math.min(1, w.distance / GOAL_DISTANCE);

      // move and prune entities
      for (const h of w.hearts) h.y += SCROLL_V * dt;
      for (const c of w.clouds) c.y += SCROLL_V * dt;
      w.hearts = w.hearts.filter((h) => h.y < SH + 50);
      w.clouds = w.clouds.filter((c) => c.y < SH + 80);

      // spawn
      w.spawnHeart += dt;
      if (w.spawnHeart > 0.7 && prog < 0.95) {
        w.spawnHeart = 0;
        w.hearts.push({ id: nextId++, x: 30 + Math.random() * (SW - 60), y: -30 });
      }
      w.spawnCloud += dt;
      const cloudInterval = Math.max(0.55, 1.4 - prog * 0.9);
      if (w.spawnCloud > cloudInterval && prog < 0.95) {
        w.spawnCloud = 0;
        w.clouds.push({
          id: nextId++,
          x: 20 + Math.random() * (SW - CLOUD_W - 40),
          y: -CLOUD_H,
          vx: (Math.random() - 0.5) * 60,
        });
      }
      for (const c of w.clouds) {
        c.x += c.vx * dt;
        if (c.x < 10) c.vx = Math.abs(c.vx);
        if (c.x > SW - CLOUD_W - 10) c.vx = -Math.abs(c.vx);
      }

      // collisions
      const ax = w.aven.x;
      const ay = w.aven.y;
      const remHearts = [];
      for (const h of w.hearts) {
        const dx = h.x - ax;
        const dy = (h.y + HEART_R) - (ay + AVEN_H / 2);
        if (dx * dx + dy * dy < (HEART_R + 26) * (HEART_R + 26)) {
          w.score += 5;
          SFX.catch();
        } else {
          remHearts.push(h);
        }
      }
      w.hearts = remHearts;

      let hitCloud = false;
      for (const c of w.clouds) {
        if (
          ax + AVEN_W / 2 > c.x + 10 &&
          ax - AVEN_W / 2 < c.x + CLOUD_W - 10 &&
          ay + AVEN_H > c.y + 8 &&
          ay < c.y + CLOUD_H - 8
        ) { hitCloud = true; break; }
      }

      forceRender((t) => (t + 1) | 0);

      if (hitCloud) { SFX.hit(); setPhase("lost"); return; }
      if (prog >= 1) { setPhase("won"); return; }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  /* commit on win/lose */
  useEffect(() => {
    if (phase === "won") {
      const w = worldRef.current;
      addScore(w.score + 200);
      clearLevel("boss", 1);
      addAchievement("reachedMama", "Reached Mama", "Cleared the boss level");
      SFX.win();
    } else if (phase === "lost") {
      SFX.lose();
    }
  }, [phase, addScore, clearLevel, addAchievement]);

  /* start */
  const start = () => {
    resumeAudio();
    SFX.click();
    worldRef.current = newWorld(sizeRef.current.w, sizeRef.current.h);
    setPhase("playing");
  };

  /* mobile button handlers — press/release.
     We also nudge position immediately on press so even a quick tap
     produces visible movement. */
  const NUDGE = 28; // px instant offset per tap
  const press = (which) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    keysRef.current[which] = true;
    const SW = sizeRef.current.w;
    const w = worldRef.current;
    if (which === "left")  w.aven.x = Math.max(AVEN_W / 2, w.aven.x - NUDGE);
    if (which === "right") w.aven.x = Math.min(SW - AVEN_W / 2, w.aven.x + NUDGE);
  };
  const release = (which) => (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    keysRef.current[which] = false;
  };
  const flapBtn = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (phaseRef.current !== "playing") return;
    worldRef.current.aven.vy = FLAP_V;
    SFX.flap();
  };

  const w = worldRef.current;
  const prog = Math.min(1, w.distance / GOAL_DISTANCE);

  return (
    <motion.div
      className="md-act md-act-game md-act-boss"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <HUD level={4} levelLabel="Reach Mama" />
      <AchievementPopup />

      <div className="md-boss-wrap">
        <div
          ref={stageRef}
          className="md-boss-stage"
          style={{ width: size.w, height: size.h }}
          onPointerDown={onStageTap}
        >
          <div className="md-boss-bg" style={{ backgroundPositionY: `${-prog * 600}px` }} />

          {phase === "playing" && (
            <GameControls
              onSkip={onNext}
              controls={[
                { keys: ["Space"], label: "Flap up · or tap the sky" },
                { keys: ["←", "→"], label: "Steer left / right · or use arrow buttons below" },
                { label: "❤ +5 each · ☁ = game over · reach 100% to find Mama" },
              ]}
            />
          )}

          <div
            className="md-boss-goal"
            style={{
              opacity: Math.max(0, prog - 0.5) * 2,
              top: 8 + (1 - prog) * -160,
            }}
          >
            <img src={MAMA_FACE} alt="Mama" />
            <div className="md-boss-goal-label">Mama ↑</div>
          </div>

          {phase === "playing" && w.hearts.map((h) => (
            <div key={h.id} className="md-boss-heart" style={{ left: h.x - HEART_R, top: h.y }}>❤</div>
          ))}
          {phase === "playing" && w.clouds.map((c) => (
            <div key={c.id} className="md-boss-cloud" style={{ left: c.x, top: c.y, width: CLOUD_W, height: CLOUD_H }}>☁</div>
          ))}

          {phase === "playing" && (
            <div
              className="md-boss-flyer"
              style={{
                left: w.aven.x - AVEN_W / 2,
                top: w.aven.y,
                width: AVEN_W,
                height: AVEN_H,
                transform: `rotate(${Math.max(-25, Math.min(35, w.aven.vy / 18))}deg)`,
              }}
            >
              <img src={w.aven.vy < -100 ? AVEN_LAUGH : AVEN_CALM} alt="Aven" />
            </div>
          )}

          <div className="md-boss-progress">
            <div className="md-boss-progress-fill" style={{ width: `${prog * 100}%` }} />
            <div className="md-boss-progress-label">{Math.round(prog * 100)}%</div>
          </div>

          {phase === "playing" && (
            <div className="md-boss-controls" onPointerDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="md-boss-ctrl md-ctrl-left"
                onPointerDown={press("left")}
                onPointerUp={release("left")}
                onPointerLeave={release("left")}
                onPointerCancel={release("left")}
                onTouchStart={press("left")}
                onTouchEnd={release("left")}
                onMouseDown={press("left")}
                onMouseUp={release("left")}
                onMouseLeave={release("left")}
                aria-label="move left"
              >←</button>
              <button
                type="button"
                className="md-boss-ctrl md-ctrl-flap"
                onPointerDown={flapBtn}
                onTouchStart={flapBtn}
                aria-label="flap up"
              >▲ flap</button>
              <button
                type="button"
                className="md-boss-ctrl md-ctrl-right"
                onPointerDown={press("right")}
                onPointerUp={release("right")}
                onPointerLeave={release("right")}
                onPointerCancel={release("right")}
                onTouchStart={press("right")}
                onTouchEnd={release("right")}
                onMouseDown={press("right")}
                onMouseUp={release("right")}
                aria-label="move right"
              >→</button>
            </div>
          )}

          <AnimatePresence>
            {phase === "ready" && (
              <motion.div className="md-game-overlay md-overlay-inset" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="md-game-card">
                  <div className="md-game-badge">Boss Level</div>
                  <h2>Reach Mama</h2>
                  <p className="md-game-tagline">
                    Aven flies up through the clouds toward Mama.<br />
                    <strong>Tap / Space</strong> to flap. <strong>Arrows</strong> or on-screen buttons to steer.
                  </p>
                  <ul className="md-game-rules">
                    <li><span>❤</span> heart <em>+5</em></li>
                    <li><span>☁</span> cloud — <em>game over</em></li>
                    <li>Reach <strong>100%</strong> to find Mama</li>
                  </ul>
                  <div className="md-game-actions">
                    <button className="md-btn-soft md-cta" onClick={start}>Take off →</button>
                    <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
                  </div>
                </div>
              </motion.div>
            )}
            {phase === "lost" && (
              <motion.div className="md-game-overlay md-overlay-inset" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="md-game-card md-game-lose">
                  <div className="md-game-badge md-badge-grey">Hit a cloud</div>
                  <h2>So close.</h2>
                  <p>You climbed <strong>{Math.round(prog * 100)}%</strong> · {w.score} hearts caught.</p>
                  <div className="md-game-actions">
                    <button className="md-btn-soft md-cta" onClick={start}>↻ Try again</button>
                    <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
                  </div>
                </div>
              </motion.div>
            )}
            {phase === "won" && (
              <motion.div className="md-game-overlay md-overlay-inset" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="md-game-card md-game-win">
                  <div className="md-game-badge md-badge-gold">Boss cleared</div>
                  <h2>You found Mama ❤</h2>
                  <div className="md-game-stats">
                    <div><span>Hearts</span><strong>{(w.score / 5) | 0}</strong></div>
                    <div><span>Score</span><strong>{w.score + 200}</strong></div>
                  </div>
                  <p className="md-game-aven-says">‘Mama, I made it. I knew I would.’</p>
                  <button className="md-btn-soft md-cta" onClick={onNext}>To the rewards →</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default BossAct;
